// comments: ContactForm with address/map + simple math captcha (+/-) using Formik/Yup
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Vector from "assets/Vectorcountact.png";
import Vector1 from "assets/phone_1.svg";
import Vector2 from "assets/Featuredicon.svg";
import LocationIcon from "../../../../assets/footer/location-1.svg";
import contentApi from "../../../../api/contentApi";
import { useTranslation } from "react-i18next";

// comments: generate a +/- challenge with non-negative subtraction
function genChallenge() {
  let a = Math.floor(Math.random() * 9) + 1; // 1..9
  let b = Math.floor(Math.random() * 9) + 1; // 1..9
  const op = Math.random() < 0.5 ? "+" : "-";
  if (op === "-" && b > a) [a, b] = [b, a];
  return { a, b, op };
}

const ContactForm = () => {
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const { t, i18n } = useTranslation("contact");

  // comments: language-specific address hint key
  const currentLang = i18n.language === "ar" ? "hint_ar" : "hint_en";

  // comments: captcha state
  const [challenge, setChallenge] = useState(genChallenge());
  const expected = useMemo(
    () => (challenge.op === "+" ? challenge.a + challenge.b : challenge.a - challenge.b),
    [challenge]
  );
const LETTERS_REGEX = /^[A-Za-z\u0600-\u06FF\s]+$/;

  // comments: Yup schema (captures `expected` from closure)
const validationSchema = Yup.object({
  first_name: Yup.string()
    .matches(LETTERS_REGEX, t("validation.lettersOnly") || "Only letters are allowed")
    .required(t("First name is required")),
  last_name: Yup.string()
    .matches(LETTERS_REGEX, t("validation.lettersOnly") || "Only letters are allowed")
    .required(t("Last name is required")),
  email: Yup.string()
    .email(t("Please enter a valid email address"))
    .required(t("Email is required")),
  phone: Yup.string()
    .matches(/^(?:966|05)\d{8}$/, t("Please enter a valid Saudi phone number (starting with +966 or 05)"))
    .required(t("Phone number is required")),
  message: Yup.string().required(t("Message is required")),
  humanAnswer: Yup.string()
    .required(t("validation.recaptcha"))
    .test("math-match", t("validation.recaptchaWrong"), (val) => {
      const num = Number(String(val || "").trim());
      return !Number.isNaN(num) && num === expected;
    }),
});


  // comments: submit handler (strip humanAnswer, refresh challenge)
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const { humanAnswer, ...payload } = values;
      await contentApi.sendContactMessage(payload);
      toast.success(t("message_sent_success"), { autoClose: 5000 });
      resetForm();
      setChallenge(genChallenge()); // new challenge next submit
    } catch (error) {
      toast.error(t("An error occurred while sending. Please try again."), { autoClose: 5000 });
      console.error(error);
      setChallenge(genChallenge()); // rotate challenge even on error
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await contentApi.getSettingData();
        setSettings(response);
      } catch (error) {
        console.error(t("An error occurred while fetching settings data:"), error);
        toast.error(t("An error occurred while fetching settings data."));
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  // comments: parse lat/lng safely; API may use "lang" for longitude
  const { lat, lng } = useMemo(() => {
    const _lat = settings?.data?.[0]?.setting?.company_information?.location?.lat;
    const _lng = settings?.data?.[0]?.setting?.company_information?.location?.lang;
    const latNum = parseFloat(_lat);
    const lngNum = parseFloat(_lng);
    return {
      lat: Number.isFinite(latNum) ? latNum : 24.7136, // Riyadh fallback
      lng: Number.isFinite(lngNum) ? lngNum : 46.6753,
    };
  }, [settings]);

  // comments: localized address string
  const address = settings?.data?.[0]?.setting?.company_information?.location?.[currentLang];

  return (
    <div className="flex lg:my-[125px] flex-col lg:flex-row gap-8 items-center bg-[#fff] overflow-hidden mx-auto w-[100%] md:[80%] max-sm:px-4 lg:px-20 py-8 relative">
      <ToastContainer />
      {/* Form Section */}
      <div className="w-full max-w-[500px] bg-[#E5F2E8] p-8 rounded-lg">
        <Formik
          initialValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",            // comments: fixed name (was "number")
            message: "",
            humanAnswer: "",       // comments: captcha answer
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form className="flex flex-col gap-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-[12px] font-medium text-[#667680] mb-2">
                  {t("First Name")}
                </label>
                <Field
                  name="first_name"
                  placeholder={t("First Name")}
                  className="w-full px-4 py-2 text-[12px] bg-[#CAE3D0] rounded-2xl focus:outline-none"
                />
                <ErrorMessage name="first_name" component="span" className="text-red-500 text-[12px]" />
              </div>
              <div className="flex-1">
                <label className="block text-[12px] font-medium text-[#667680] mb-2">
                  {t("Last Name")}
                </label>
                <Field
                  name="last_name"
                  placeholder={t("Last Name")}
                  className="w-full px-4 py-2 bg-[#CAE3D0] rounded-2xl focus:outline-none"
                />
                <ErrorMessage name="last_name" component="span" className="text-red-500 text-[12px]" />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#667680] mb-2">
                {t("Email")}
              </label>
              <Field
                name="email"
                type="email"
                placeholder="you@company.com"
                className="lowercase w-full px-4 py-2 bg-[#CAE3D0] rounded-2xl focus:outline-none"
              />
              <ErrorMessage name="email" component="span" className="text-red-500 text-[12px]" />
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#667680] mb-2">
                {t("Phone Number")}
              </label>
              <Field
                name="phone"
                placeholder="05XXXXXXXX أو +9665XXXXXXXX"
                className="w-full px-4 py-2 bg-[#CAE3D0] rounded-2xl focus:outline-none"
              />
              <ErrorMessage name="phone" component="span" className="text-red-500 text-[12px]" />
            </div>

            <div>
              <label className="block text-[12px] font-medium text-[#667680] mb-2">
                {t("Message")}
              </label>
              <Field
                name="message"
                as="textarea"
                placeholder={t("Leave us a message...")}
                rows={4}
                className="w-full px-4 py-2 bg-[#CAE3D0] rounded-lg focus:outline-none"
              />
              <ErrorMessage name="message" component="span" className="text-red-500 text-[12px]" />
            </div>

            {/* comments: Simple math captcha */}
            <div>
              <label className="block text-[12px] font-medium text-[#667680] mb-2">
                {t("fields.recaptcha")} ({challenge.a} {challenge.op} {challenge.b} = ?)
              </label>
              <Field
                name="humanAnswer"
                inputMode="numeric"
                placeholder={t("placeholders.recaptcha")}
                className="w-full px-4 py-2 bg-[#CAE3D0] rounded-2xl focus:outline-none text-right"
              />
              <ErrorMessage name="humanAnswer" component="span" className="text-red-500 text-[12px]" />
            </div>

            <button type="submit" className="w-full bg-[#0d6b70] text-white py-3 rounded-2xl text-center font-medium">
              {t("Send")}
            </button>
          </Form>
        </Formik>
      </div>

      {/* Right: Address + Map + Contacts */}
      <div className="flex flex-col gap-8 items-center justify-between w-full max-w-screen-xl">
        <div className="w-full lg:w-[591px] h-[250px] rounded-md">
          {/* address row */}
          <div className="flex items-center gap-2 mb-3">
            <img src={LocationIcon} alt="location-icon" className="w-5 h-5" />
            <span className="text-[#0d6b70] text-sm font-medium">{t("address")}:</span>
            <span className="text-[#94969c] text-xs sm:text-sm line-clamp-2">
              {address || "-"}
            </span>
          </div>

          {/* map */}
          <div className="w-full h-[200px] rounded-[16px] overflow-hidden border border-[#E6EEF6] shadow-[0_1px_0_rgba(16,24,40,0.04)] bg-[#F7FBFE]">
            <iframe
              src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
              className="w-full h-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="company-map"
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Phone */}
          <div className="flex flex-col items-center gap-4 text-center px-5">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center">
              <img src={Vector1} alt="Phone Icon" />
            </div>
            <div className="flex flex-col gap-2 items-center">
              <span className="text-[1rem] font-semibold text-[#1d1d1d]">
                {t("Phone Call")}
              </span>
              <span className="text-[14px] text-[#94969c] leading-6 w-[58%]">
                {settings?.data?.[0]?.setting?.company_information?.phone?.[currentLang]}
              </span>
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col items-center gap-4 text-center px-5">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center">
              <img src={Vector2} alt="Email Icon" />
            </div>
            <div className="flex flex-col gap-2 items-center">
              <span className="text-[1rem] font-semibold text-[#1d1d1d]">
                {t("Email")}
              </span>
              <span className="text-[14px] text-[#94969c] leading-6 w-[58%] lowercase">
                {settings?.data?.[0]?.setting?.company_information?.email?.[currentLang]}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
